import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getRoutePath, ROUTE_IDS } from '@/app/router/route-registry';
import { getAdminTenant } from '@/modules/admin/services/admin-tenants.service';
import { listAdminAssets, uploadAdminAsset } from '@/modules/admin/services/admin-assets.service';

export default function AdminControlPlaneClientDetailPage() {
  const { tenantId = '' } = useParams();
  const [tenant, setTenant] = useState(null);
  const [error, setError] = useState('');
  const [assetError, setAssetError] = useState('');
  const [assets, setAssets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const clientsPath =
    (ROUTE_IDS.ADMIN_CONTROL_PLANE_CLIENTS
      ? getRoutePath(ROUTE_IDS.ADMIN_CONTROL_PLANE_CLIENTS)
      : null) || '/admin/clients';

  useEffect(() => {
    let active = true;

    async function load() {
      const result = await getAdminTenant(tenantId);
      if (!active) {
        return;
      }

      if (!result.ok) {
        setError(result.message);
        setTenant(null);
        setIsLoading(false);
        return;
      }

      setTenant(result.data.tenant);
      const assetsResult = await listAdminAssets({ tenantId });
      if (active && assetsResult.ok) {
        setAssets(assetsResult.data.assets);
      }
      setError('');
      setIsLoading(false);
    }

    load();

    return () => {
      active = false;
    };
  }, [tenantId]);

  async function handleTenantAssetUpload(event, options) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setAssetError('');
    const result = await uploadAdminAsset({
      file,
      tenantId,
      autoAssign: true,
      ...options,
    });
    if (!result.ok) {
      setAssetError(result.message);
      return;
    }
    const assetsResult = await listAdminAssets({ tenantId });
    if (assetsResult.ok) {
      setAssets(assetsResult.data.assets);
    }
  }

  return (
    <section className="control-plane-page">
      <header className="control-plane-topbar">
        <div>
          <div className="control-plane-topbar__title">{tenant?.name || tenantId}</div>
          <div className="control-plane-topbar__subtitle">
            Scheda cliente derivata dal tenant reale del control plane
          </div>
        </div>
        <div className="control-plane-topbar__actions">
          <Link className="control-plane-button" to={clientsPath}>
            Torna ai clienti
          </Link>
        </div>
      </header>

      <div className="control-plane-content">
        {isLoading ? <div className="control-plane-empty-state">Caricamento scheda cliente...</div> : null}
        {!isLoading && error ? <div role="alert" className="control-plane-inline-error wt-inline-error">{error}</div> : null}
        {!isLoading && tenant ? (
          <>
            <section className="control-plane-metrics control-plane-metrics--4">
              <article className="control-plane-metric">
                <div className="control-plane-metric__label">Attivazione</div>
                <div className="control-plane-metric__value">—</div>
                <div className="control-plane-metric__support">dato non disponibile</div>
              </article>
              <article className="control-plane-metric">
                <div className="control-plane-metric__label">Piani mensili</div>
                <div className="control-plane-metric__value">—</div>
                <div className="control-plane-metric__support">backend non ancora collegato</div>
              </article>
              <article className="control-plane-metric">
                <div className="control-plane-metric__label">Add-on automazioni</div>
                <div className="control-plane-metric__value">—</div>
                <div className="control-plane-metric__support">non vendibile</div>
              </article>
              <article className="control-plane-metric">
                <div className="control-plane-metric__label">Totale ricorrente</div>
                <div className="control-plane-metric__value">—</div>
                <div className="control-plane-metric__support">placeholder operativo</div>
              </article>
            </section>

            <section className="control-plane-split-grid">
              <section className="control-plane-card">
                <div className="control-plane-card__header">
                  <div>
                    <div className="control-plane-card__title">Brand e asset tenant</div>
                    <div className="control-plane-card__subtitle">Logo sidebar e template iniziali assegnati al cliente.</div>
                  </div>
                </div>
                {assetError ? <div role="alert" className="control-plane-inline-error wt-inline-error">{assetError}</div> : null}
                <div className="control-plane-form-row">
                  <label className="control-plane-field">
                    <span>Sostituisci logo sidebar</span>
                    <input
                      type="file"
                      accept="image/svg+xml,image/png,image/webp,image/jpeg"
                      onChange={(event) => handleTenantAssetUpload(event, {
                        scope: 'TENANT_BRAND',
                        usageKey: 'tenant_sidebar_logo',
                        displayName: 'Logo sidebar tenant',
                      })}
                    />
                  </label>
                  <label className="control-plane-field">
                    <span>Carica template generico</span>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      onChange={(event) => handleTenantAssetUpload(event, {
                        scope: 'TENANT_TEMPLATE',
                        usageKey: 'tenant_generic_document_template',
                        displayName: 'Template generico',
                      })}
                    />
                  </label>
                </div>
                <div className="control-plane-associated-list" style={{ marginTop: '16px' }}>
                  {assets.map((asset) => (
                    <article key={asset.id} className="control-plane-associated-item">
                      <div className="control-plane-associated-item__title">{asset.displayName}</div>
                      <div className="control-plane-associated-item__meta">{asset.scope} · {asset.usageKey} · {asset.originalFilename}</div>
                      {asset.publicUrl ? (
                        <img src={asset.publicUrl} alt={asset.displayName} style={{ maxHeight: '40px', maxWidth: '160px', marginTop: '8px' }} />
                      ) : null}
                    </article>
                  ))}
                  {assets.length === 0 ? <div className="control-plane-empty-state">Nessun asset assegnato.</div> : null}
                </div>
              </section>

              <section className="control-plane-card">
                <div className="control-plane-card__header">
                  <div>
                    <div className="control-plane-card__title">Scheda cliente</div>
                    <div className="control-plane-card__subtitle">Dati reali esposti dal backend admin tenant.</div>
                  </div>
                </div>
                <div className="control-plane-form-row control-plane-form-row--3">
                  <div className="control-plane-field"><span>Tenant ID</span><input disabled value={tenant.id} /></div>
                  <div className="control-plane-field"><span>Slug</span><input disabled value={tenant.slug || '—'} /></div>
                  <div className="control-plane-field"><span>Piano</span><input disabled value={tenant.planCode || '—'} /></div>
                </div>
                <div className="control-plane-form-row control-plane-form-row--3" style={{ marginTop: '12px' }}>
                  <div className="control-plane-field"><span>Provisioning</span><input disabled value={tenant.provisioningStatus || '—'} /></div>
                  <div className="control-plane-field"><span>Database</span><input disabled value={tenant.databaseName || '—'} /></div>
                  <div className="control-plane-field"><span>Owner invite</span><input disabled value={tenant.ownerInviteStatus || '—'} /></div>
                </div>
                <div className="control-plane-form-row" style={{ marginTop: '12px' }}>
                  <div className="control-plane-field"><span>Migration</span><input disabled value={tenant.migrationStatus || '—'} /></div>
                  <div className="control-plane-field"><span>Moduli</span><input disabled value={tenant.enabledModuleKeys.join(', ') || '—'} /></div>
                </div>
              </section>

              <section className="control-plane-card">
                <div className="control-plane-card__header">
                  <div>
                    <div className="control-plane-card__title">Gestionali associati</div>
                    <div className="control-plane-card__subtitle">Tenant reale corrente e collegamenti disponibili.</div>
                  </div>
                </div>
                <div className="control-plane-associated-list">
                  <article className="control-plane-associated-item">
                    <div className="control-plane-associated-item__title">{tenant.name}</div>
                    <div className="control-plane-associated-item__meta">{tenant.slug || tenant.id}</div>
                    <div className="control-plane-associated-item__meta">Provisioning: {tenant.provisioningStatus || '—'}</div>
                  </article>
                </div>
              </section>
            </section>

            <section className="control-plane-split-grid">
              <section className="control-plane-card">
                <div className="control-plane-card__header">
                  <div>
                    <div className="control-plane-card__title">Automazioni</div>
                    <div className="control-plane-card__subtitle">Non vendibile / backend non presente.</div>
                  </div>
                </div>
                <div className="control-plane-empty-state">Dato non disponibile.</div>
              </section>

              <section className="control-plane-card">
                <div className="control-plane-card__header">
                  <div>
                    <div className="control-plane-card__title">Breakdown spesa</div>
                    <div className="control-plane-card__subtitle">Billing platform non ancora collegato.</div>
                  </div>
                </div>
                <div className="control-plane-empty-state">Placeholder operativo.</div>
              </section>
            </section>
          </>
        ) : null}
      </div>
    </section>
  );
}
